import { EManagementType, EProcessNames, ERowStatus } from "api/enums";
import { AmountCannotBeLessThanAccumulatedException } from "api/exceptions/AmountCannotBeLessThanAccumulatedException";
import clsx from "clsx";
import { PageLoading } from "components/UI/Page";
import {
  CellAction,
  CellTooltip,
} from "components/shared/Contract/TableStickers";
import { IColumn } from "components/shared/MuiVirtualizedTable/Cells";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { inputTextToNumber } from "utils";
import {
  AccessProfileStore,
  ContractRowMovementStore,
  ContractRowStore,
  ContractStore,
  MessageStore,
} from "../../store";
import { columns } from "../shared/Contract/assets/columnsData";
import useStyles from "../shared/Contract/assets/stylesTable";
import { AutoSizedTable } from "../shared/MuiVirtualizedTable";

const ContractTable = observer(() => {
  const classes = useStyles();
  const openLeftMenu = ContractStore.openLeftSideMenu;
  const contract = ContractStore.getCurrentContract();
  const rowsCount = ContractRowStore.rowsCount;
  const selectedRowIndex = ContractRowStore.getSelectedRowIndex();
  const defaultRowsMin = 30;

  const handleTableRowGetter = ({ index }) => {
    if (rowsCount === 0) return ContractRowStore.getBlanckRow(index);

    const rowData: any = ContractRowStore.getRowFormated(index);
    if (!rowData) return {};

    if (rowData.isStage) {
      rowData.customClass = classes.stageRow;
    } else {
      rowData.customClass = clsx({
        "MuiVirtualizedTable-cell-selected": rowData?.selected,
        "MuiVirtualizedTable-cell-canceled":
          rowData?.status === ERowStatus.CANCELED,
      });
    }

    rowData.cellCustoms = {
      description: {
        style: {
          textIndent: `${rowData.level * 0.25}rem`,
        },
      },
    };

    return rowData;
  };

  const handleChangeInput = (rowIndex, dataKey, value) => {
    if (typeof value === "string") value = inputTextToNumber(value);
    return value === 0 ? "" : value;
  };

  const handleChangeQuantityValue = (rowIndex, dataKey, value) => {
    if (
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.EDITAR_CONTRATO
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }

    if (dataKey !== "quantity") return;

    value = Number(inputTextToNumber(value));
    if (isNaN(value)) return;

    ContractRowStore.setTablesLoading(true);
    ContractRowMovementStore.updateRowsQuantity(rowIndex, value)
      .catch((error) => {
        if (error instanceof AmountCannotBeLessThanAccumulatedException) {
          MessageStore.error(lang("contract.errors.quantityLessThanBalance"));
        } else {
          MessageStore.error(lang("contract.errors.updateQuantity"));
        }
      })
      .finally(() => ContractRowStore.setTablesLoading(false));
  };

  const configureTablesColumns = () => {
    const columnsConfig: IColumn[] = [
      ...columns,
      ...ContractStore.customPropertiesList.map((prop) => ({
        width: 120,
        label: prop.label,
        dataKey: prop.name,
        /** Alinha as colunas com o titulo quando o mesmo é grande
         * @ignore */
        flexGrow: prop.name.length > 12 ? 1 : 0,
        cellComponent: CellTooltip,
        headComponent: CellTooltip,
      })),
    ];
    columnsConfig[2].input =
      contract?.managementType === EManagementType.UNIT_PRICE;

    if (contract?.hasChanged) {
      columnsConfig.push({
        width: 96,
        label: lang("actions"),
        dataKey: "action",
        align: "center" as any,
        cellComponent: CellAction,
      });
    }

    return columnsConfig;
  };

  const columnsConfig = configureTablesColumns();
  let props = selectedRowIndex >= 0 ? { scrollToIndex: selectedRowIndex } : {};

  return (
    <>
      {ContractRowStore.tableLoading && (
        <PageLoading className={classes.loading} />
      )}
      <AutoSizedTable
        tableClassName={clsx(classes.tableContract, {
          selected: selectedRowIndex >= 0,
          [classes.tableRetractSize]: openLeftMenu,
          [classes.tableSize]: !openLeftMenu,
        })}
        rowCount={!rowsCount ? defaultRowsMin : rowsCount}
        rowGetter={handleTableRowGetter}
        rowHeight={34}
        headCellClassName={classes.tableHeadCell}
        cellClassName={classes.tableCell}
        columns={columnsConfig}
        scrollToAlignment="start"
        onChangeInputCell={handleChangeInput}
        onChangeInputCellValue={handleChangeQuantityValue}
        {...props}
      />
    </>
  );
});

export default ContractTable;
